import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { config } from '@/config';
import { prepareHeaders } from '@/configuration/tokenHandling/prepareHeaders';
import { Criticality, VehicleWarning } from '@/features/warnings/utils/types';
import { buildWarningView } from '@/features/warnings/utils/warningCodeMessages';
import {
    SummaryRequest,
    VehicleWarningResponse,
    type VehicleWarnings,
    WarningSummary,
} from '@/generated/GeneratedApiTypes';

type VehicleWarningsParams = VehicleWarnings.GetVehicleWarnings.RequestParams &
    VehicleWarnings.GetVehicleWarnings.RequestQuery;

// The API response with each item enriched with the computed `messages`
type VehicleWarningsResponse = Omit<VehicleWarningResponse, 'items'> & {
    items: VehicleWarning[];
};

export const warningsApi = createApi({
    reducerPath: 'warningsApi',
    baseQuery: fetchBaseQuery({ baseUrl: config.backend.BACKEND_URI, prepareHeaders }),
    endpoints: (build) => ({
        getVehicleWarnings: build.query<VehicleWarningsResponse, VehicleWarningsParams>({
            query: ({ assetId, limit, offset, criticality }) => {
                const params = new URLSearchParams();
                if (limit !== undefined) {
                    params.set('limit', String(limit));
                }
                if (offset !== undefined) {
                    params.set('offset', String(offset));
                }
                if (criticality && criticality.length > 0) {
                    criticality.forEach((c) => params.append('criticality', c));
                }
                const queryString = params.toString();
                return queryString ? `/vehicle-warnings/${assetId}?${queryString}` : `/vehicle-warnings/${assetId}`;
            },
            transformResponse: (response: VehicleWarningResponse): VehicleWarningsResponse => ({
                ...response,
                items: response.items.map((item) => ({
                    ...item,
                    criticality: item.criticality as Criticality,
                    messages: buildWarningView(item),
                })),
            }),
        }),
        getVehicleWarningSummary: build.query<WarningSummary, { assetId?: string }>({
            // POST is used because this endpoint is shared with the man-fleetstatus-aggregation-backend,
            // which needs to pass a list of asset IDs in the request body.
            query: ({ assetId }) => ({
                url: '/vehicle-warnings/summary',
                method: 'POST',
                body: (assetId ? { asset_ids: [assetId] } : {}) satisfies SummaryRequest,
            }),
        }),
    }),
});

export const { useGetVehicleWarningsQuery, useLazyGetVehicleWarningsQuery, useGetVehicleWarningSummaryQuery } =
    warningsApi;
